import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState, type ReactNode } from "react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Modal } from "../components/ui/Modal/Modal";
import { FormField } from "../components/ui/FormField/FormField";
import { Input } from "../components/ui/Input/Input";
import { Select } from "../components/ui/Select/Select";
import { SearchableSelect } from "../components/ui/SearchableSelect/SearchableSelect";
import { ConfirmDialog } from "../components/ui/ConfirmDialog/ConfirmDialog";
import { OrderAttachmentsModal } from "../components/OrderAttachmentsModal/OrderAttachmentsModal";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import {
	useOrderDetail,
	useDeleteOrder,
	useCancelOrder,
	useCloseOrder,
	useUpdateOrder,
} from "../hooks/use-orders";
import { useDelegation } from "../hooks/use-delegation";
import { useToast } from "../lib/use-toast";
import { useAuth } from "../lib/use-auth";
import { usePaymentList, useCreatePayment } from "../hooks/use-payments";
import { BackButton } from "../components/ui/BackButton/BackButton";
import { formatDate, formatAmount } from "../lib/date";
import styles from "./OrderDetail.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/orders/$id",
	component: withPermission(OrderDetailPage, "orders", "read"),
});

function OrderDetailPage() {
	const { id } = route.useParams();
	const { t } = useTranslation();
	const orderQuery = useOrderDetail(id);
	const deleteOrder = useDeleteOrder();
	const cancelOrder = useCancelOrder();
	const closeOrder = useCloseOrder();
	const updateOrder = useUpdateOrder();
	const { canDelegate, users: delegatableUsers } = useDelegation();
	const { user } = useAuth();
	const navigate = useNavigate();
	const { showToast } = useToast();
	const paymentsQuery = usePaymentList(id);
	const createPayment = useCreatePayment();
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [cancelOpen, setCancelOpen] = useState(false);
	const [closeOpen, setCloseOpen] = useState(false);
	const [duplicateOpen, setDuplicateOpen] = useState(false);
	const [attachmentsOpen, setAttachmentsOpen] = useState(false);
	const [paymentOpen, setPaymentOpen] = useState(false);
	const [salespersonOpen, setSalespersonOpen] = useState(false);
	const [newSalesperson, setNewSalesperson] = useState("");
	const [paymentData, setPaymentData] = useState<{
		amount: string;
		currency: string;
		method: string;
		reference: string;
		notes: string;
		receivedAt: string;
	}>({ amount: "", currency: "", method: "cash", reference: "", notes: "", receivedAt: "" });
	const [paymentError, setPaymentError] = useState("");

	if (orderQuery.isLoading) return <LoadingSpinner />;
	if (orderQuery.isError || !orderQuery.data) return <ErrorFallback onRetry={orderQuery.refetch} />;

	const order = orderQuery.data;

	const openPayment = () => {
		setPaymentData({
			amount: "",
			currency: order.currency ?? "",
			method: "cash",
			reference: "",
			notes: "",
			receivedAt: new Date().toISOString().slice(0, 10),
		});
		setPaymentOpen(true);
	};

	const savePayment = () => {
		setPaymentError("");
		if (!paymentData.amount || Number(paymentData.amount) <= 0) {
			setPaymentError(t("fillRequiredFields"));
			return;
		}
		createPayment.mutate(
			{
				orderId: id,
				amount: Number(paymentData.amount),
				currency: paymentData.currency || undefined,
				method: paymentData.method as
					| "cash"
					| "bank_transfer"
					| "credit_card"
					| "check"
					| "letter_of_credit"
					| "other",
				reference: paymentData.reference || undefined,
				notes: paymentData.notes || undefined,
				receivedAt: paymentData.receivedAt || undefined,
			},
			{
				onSuccess: () => {
					setPaymentOpen(false);
					alert(t("createSuccess"));
				},
				onError: (err) => setPaymentError(err instanceof Error ? err.message : t("error")),
			},
		);
	};

	const openSalesperson = () => {
		setNewSalesperson(order.assignedTo ?? "");
		setSalespersonOpen(true);
	};

	const saveSalesperson = () => {
		if (!newSalesperson || newSalesperson === order.assignedTo) {
			setSalespersonOpen(false);
			return;
		}
		updateOrder.mutate(
			{ id, data: { assignedTo: newSalesperson } },
			{
				onSuccess: () => {
					setSalespersonOpen(false);
					showToast(t("changeSalespersonSuccess"), "success");
				},
				onError: (err) => {
					showToast(err instanceof Error ? err.message : t("error"), "error");
				},
			},
		);
	};

	const paid = Number(order.paidAmount ?? 0);
	const total = Number(order.total ?? 0);
	const remaining = Math.max(total - paid, 0);
	const percent = total > 0 ? Math.min((paid / total) * 100, 100) : 0;

	return (
		<div>
			<BackButton fallbackTo="/orders" />
			<PageHeader
				title={
					order.sapDocNum != null
						? String(order.sapDocNum)
						: order.documentNumber || `${t("order")} #${order.id.slice(0, 8)}`
				}
				{...(order.sapDocNum != null &&
				order.documentNumber &&
				order.documentNumber !== String(order.sapDocNum)
					? { subtitle: `CRM ${order.documentNumber}` }
					: {})}
				action={
					<div className={styles.actions}>
						<Link
							to="/contracts/new"
							search={{
								companyId: order.companyId,
								orderId: id,
							}}
						>
							<Button variant="secondary">{t("createContract")}</Button>
						</Link>
						{order.status !== "cancelled" && (
							<Link to="/orders/$id/edit" params={{ id: order.id }}>
								<Button>{t("edit")}</Button>
							</Link>
						)}
						{order.status !== "cancelled" && (
							<Button variant="secondary" onClick={() => setCancelOpen(true)}>
								{t("cancelDocument")}
							</Button>
						)}
						{order.status !== "cancelled" &&
							order.status !== "closed" &&
							order.status !== "draft" && (
								<Button variant="secondary" onClick={() => setCloseOpen(true)}>
									{t("closeOrder")}
								</Button>
							)}
						{(order.status !== "draft" || user?.role === "admin") && (
							<Button
								variant={order.status === "cancelled" ? "primary" : "secondary"}
								onClick={() => setDuplicateOpen(true)}
							>
								{t("duplicateOrder")}
							</Button>
						)}
						<Button variant="secondary" onClick={() => setAttachmentsOpen(true)}>
							{t("attachments")}
						</Button>
						<Button variant="danger" onClick={() => setDeleteOpen(true)}>
							{t("delete")}
						</Button>
					</div>
				}
			/>

			<div className={styles.grid}>
				<Card header={t("generalInfo")}>
					<div className={styles.infoGrid}>
						<Info
							label={t("company")}
							value={
								order.companyId ? (
									<Link
										to={`/companies/${order.companyId}` as never}
										search={
											(order.companySapAccountId
												? { account: order.companySapAccountId }
												: undefined) as never
										}
										className={styles.link}
									>
										{order.companyName ?? "-"}
										{order.companySapCardCode ? ` (${order.companySapCardCode})` : ""}
									</Link>
								) : (
									(order.companyName ?? "-")
								)
							}
						/>
						<Info label={t("shippingAddress")} value={order.shippingAddress ?? "-"} />
						<Info label={t("orderGeneralDescription")} value={order.notes ?? "-"} />
						<Info
							label={t("orderDate")}
							value={order.orderDate ? formatDate(order.orderDate) : "-"}
						/>
						<Info
							label={t("deliveryDate")}
							value={order.deliveryDate ? formatDate(order.deliveryDate) : "-"}
						/>
						<Info label={t("currency")} value={order.currency} />
						<Info label={t("paymentTerms")} value={order.paymentTerms} />
						<Info label={t("deliveryTerms")} value={order.deliveryTerms} />
						<div>
							<span className={styles.infoLabel}>{t("salesPerson")}</span>
							<div className={styles.valueRow}>
								<p className={styles.infoValue}>{order.assignedToName ?? "-"}</p>
								{canDelegate && order.status !== "cancelled" && (
									<Button variant="ghost" size="sm" onClick={openSalesperson}>
										{t("change")}
									</Button>
								)}
							</div>
						</div>
						<div>
							<span className={styles.infoLabel}>{t("status")}</span>
							<span
								style={{
									color:
										order.status === "cancelled"
											? "#ef4444"
											: order.status === "closed" || order.status === "draft"
												? "#6b7280"
												: "#22c55e",
									fontWeight: 600,
								}}
							>
								{order.status === "cancelled"
									? "İptal"
									: order.status === "closed"
										? "Kapalı"
										: order.status === "draft"
											? t("draft")
											: "Açık"}
							</span>
						</div>
						<Info
							label={t("paymentStatus")}
							value={order.paymentStatus ? t(order.paymentStatus) : "-"}
						/>
						{order.copiedFrom && (
							<div>
								<span className={styles.infoLabel}>{t("copiedFromOrder")}</span>
								<Link
									to="/orders/$id"
									params={{ id: order.copiedFrom.id }}
									className={styles.link}
								>
									{order.copiedFrom.documentNumber ?? t("viewDetails")}
								</Link>
							</div>
						)}
						{order.copies.length > 0 && (
							<div>
								<span className={styles.infoLabel}>{t("replacementOrders")}</span>
								<span>
									{order.copies.map((copy, i) => (
										<span key={copy.id}>
											{i > 0 && ", "}
											<Link to="/orders/$id" params={{ id: copy.id }} className={styles.link}>
												{copy.documentNumber ?? t("viewDetails")}
											</Link>
										</span>
									))}
								</span>
							</div>
						)}
						{order.sourceOfferId && (
							<div>
								<span className={styles.infoLabel}>{t("offer")}</span>
								<Link to={`/offers/${order.sourceOfferId}` as never} className={styles.link}>
									{t("viewDetails")}
								</Link>
							</div>
						)}
					</div>
				</Card>

				<Card header={t("lineItems")}>
					{(order.lines ?? []).length === 0 && <p className={styles.empty}>{t("noData")}</p>}
					<table className={styles.lineTable}>
						<thead>
							<tr>
								<th>{t("itemCode")}</th>
								<th>{t("itemDescription")}</th>
								<th>{t("displayDescription")}</th>
								<th>{t("quantity")}</th>
								<th>{t("unit")}</th>
								<th>{t("unitPrice")}</th>
								<th>{t("lineTotal")}</th>
								<th>{t("warehouse")}</th>
								<th>{t("isCustomDesign")}</th>
							</tr>
						</thead>
						<tbody>
							{(order.lines ?? []).map((line) => (
								<tr key={line.id}>
									<td>{line.itemCode || "-"}</td>
									<td>{line.itemDescription || "-"}</td>
									<td>{line.displayDescription || line.itemDescription || "-"}</td>
									<td>{line.quantity}</td>
									<td>{line.unit || "-"}</td>
									<td>{formatAmount(line.unitPrice)}</td>
									<td>{formatAmount(line.lineTotal)}</td>
									<td>
										{line.warehouseCode && line.warehouseName
											? `${line.warehouseCode} - ${line.warehouseName}`
											: line.warehouseName || line.warehouseCode || "-"}
									</td>
									<td>{line.isCustomDesign ? t("yes") : t("no")}</td>
								</tr>
							))}
						</tbody>
					</table>
				</Card>

				<Card
					header={
						<div className={styles.cardHeader}>
							<span>{t("payments")}</span>
							<Button variant="secondary" size="sm" onClick={openPayment}>
								{t("addPayment")}
							</Button>
						</div>
					}
				>
					<div className={styles.progressWrap}>
						<div className={styles.progressBar}>
							<div className={styles.progressFill} style={{ width: `${percent}%` }} />
						</div>
						<div className={styles.progressLabel}>
							{t("paid")}: {formatAmount(order.paidAmount)} / {formatAmount(order.total)}{" "}
							{order.currency} ({t("remaining")}: {formatAmount(remaining)})
						</div>
					</div>
					{paymentsQuery.isLoading && <LoadingSpinner />}
					{paymentsQuery.isError && <ErrorFallback onRetry={paymentsQuery.refetch} />}
					{paymentsQuery.data && paymentsQuery.data.items.length === 0 && (
						<p className={styles.empty}>{t("noData")}</p>
					)}
					{paymentsQuery.data && paymentsQuery.data.items.length > 0 && (
						<table className={styles.lineTable}>
							<thead>
								<tr>
									<th>{t("receivedAt")}</th>
									<th>{t("amount")}</th>
									<th>{t("method")}</th>
									<th>{t("reference")}</th>
									<th>{t("recordedBy")}</th>
								</tr>
							</thead>
							<tbody>
								{paymentsQuery.data.items.map((payment) => (
									<tr key={payment.id}>
										<td>{payment.receivedAt ? formatDate(payment.receivedAt) : "-"}</td>
										<td>
											{formatAmount(payment.amount)} {payment.currency ?? order.currency ?? ""}
										</td>
										<td>{t(payment.method)}</td>
										<td>{payment.reference || "-"}</td>
										<td>{payment.recordedByName || "-"}</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</Card>

				<Card>
					<div className={styles.totals}>
						<div className={styles.totalRow}>
							<span>{t("subtotal")}</span>
							<span>
								{formatAmount(order.subtotal)} {order.currency}
							</span>
						</div>
						<div className={styles.totalRow}>
							<span>{t("vatTotal")}</span>
							<span>
								{formatAmount(order.vatTotal)} {order.currency}
							</span>
						</div>
						<div className={styles.totalRow}>
							<span>{t("paidAmount")}</span>
							<span>
								{formatAmount(order.paidAmount)} {order.currency}
							</span>
						</div>
						<div className={[styles.totalRow, styles.grandTotal].join(" ")}>
							<span>{t("total")}</span>
							<span>
								{formatAmount(order.total)} {order.currency}
							</span>
						</div>
					</div>
				</Card>
			</div>

			<Modal isOpen={paymentOpen} onClose={() => setPaymentOpen(false)} title={t("addPayment")}>
				{paymentError && <div className={styles.formError}>{paymentError}</div>}
				<FormField label={t("amount")}>
					<Input
						type="number"
						value={paymentData.amount}
						onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
					/>
				</FormField>
				<FormField label={t("currency")}>
					<Input
						value={paymentData.currency}
						onChange={(e) => setPaymentData({ ...paymentData, currency: e.target.value })}
					/>
				</FormField>
				<FormField label={t("method")}>
					<Select
						options={[
							{ value: "cash", label: t("cash") },
							{ value: "bank_transfer", label: t("bankTransfer") },
							{ value: "credit_card", label: t("creditCard") },
							{ value: "check", label: t("check") },
							{ value: "letter_of_credit", label: t("letterOfCredit") },
							{ value: "other", label: t("other") },
						]}
						value={paymentData.method}
						onChange={(e) => setPaymentData({ ...paymentData, method: e.target.value })}
					/>
				</FormField>
				<FormField label={t("reference")}>
					<Input
						value={paymentData.reference}
						onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
					/>
				</FormField>
				<FormField label={t("notes")}>
					<Input
						value={paymentData.notes}
						onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
					/>
				</FormField>
				<FormField label={t("receivedAt")}>
					<Input
						type="date"
						value={paymentData.receivedAt}
						onChange={(e) => setPaymentData({ ...paymentData, receivedAt: e.target.value })}
					/>
				</FormField>
				<div className={styles.modalActions}>
					<Button variant="ghost" onClick={() => setPaymentOpen(false)}>
						{t("cancel")}
					</Button>
					<Button onClick={savePayment} isLoading={createPayment.isPending}>
						{t("save")}
					</Button>
				</div>
			</Modal>

			<Modal
				isOpen={salespersonOpen}
				onClose={() => setSalespersonOpen(false)}
				title={t("changeSalesperson")}
			>
				<FormField label={t("salesPerson")}>
					<SearchableSelect
						options={delegatableUsers.map((u) => ({ value: u.id, label: u.name }))}
						value={newSalesperson}
						onChange={(val) => setNewSalesperson(val)}
						placeholder={t("onBehalfOfSelf")}
					/>
				</FormField>
				<div className={styles.modalActions}>
					<Button variant="ghost" onClick={() => setSalespersonOpen(false)}>
						{t("cancel")}
					</Button>
					<Button
						onClick={saveSalesperson}
						isLoading={updateOrder.isPending}
						disabled={!newSalesperson}
					>
						{t("save")}
					</Button>
				</div>
			</Modal>

			<ConfirmDialog
				isOpen={duplicateOpen}
				onClose={() => setDuplicateOpen(false)}
				onConfirm={() => {
					setDuplicateOpen(false);
					showToast(t("duplicateOrderSuccess"), "success");
					navigate({ to: "/orders/new", search: { copyFrom: id } as never });
				}}
				message={t("confirmDuplicateOrder")}
			/>

			<ConfirmDialog
				isOpen={cancelOpen}
				onClose={() => setCancelOpen(false)}
				onConfirm={() =>
					cancelOrder.mutate(id, {
						onSuccess: () => {
							setCancelOpen(false);
							showToast(t("cancelDocumentSuccess"), "success");
						},
						onError: (err) => {
							setCancelOpen(false);
							showToast(err instanceof Error ? err.message : t("error"), "error");
						},
					})
				}
				message={t("confirmCancelOrder")}
				isLoading={cancelOrder.isPending}
			/>

			<ConfirmDialog
				isOpen={closeOpen}
				onClose={() => setCloseOpen(false)}
				onConfirm={() =>
					closeOrder.mutate(id, {
						onSuccess: () => {
							setCloseOpen(false);
							showToast(t("closeOrderSuccess"), "success");
						},
						onError: (err) => {
							setCloseOpen(false);
							showToast(err instanceof Error ? err.message : t("error"), "error");
						},
					})
				}
				message={t("confirmCloseOrder")}
				isLoading={closeOrder.isPending}
			/>

			<ConfirmDialog
				isOpen={deleteOpen}
				onClose={() => setDeleteOpen(false)}
				onConfirm={() =>
					deleteOrder.mutate(id, {
						onSuccess: () => {
							setDeleteOpen(false);
							window.location.href = "/orders";
						},
						onError: (err) => {
							setDeleteOpen(false);
							showToast(err instanceof Error ? err.message : t("error"), "error");
						},
					})
				}
				message={t("confirmDelete")}
				isLoading={deleteOrder.isPending}
			/>

			<OrderAttachmentsModal
				orderId={attachmentsOpen ? order.id : null}
				documentNumber={order.documentNumber}
				onClose={() => setAttachmentsOpen(false)}
			/>
		</div>
	);
}

function Info({ label, value }: { label: string; value: ReactNode }) {
	return (
		<div>
			<span className={styles.infoLabel}>{label}</span>
			<p className={styles.infoValue}>{value || "-"}</p>
		</div>
	);
}
